/**
 * Чистая логика механики «Добери до суммы — получи подарок» (рост среднего чека).
 * Без Vue/сторов — легко тестируется и переиспользуется.
 *
 * База порогов = сумма ОПЛАЧИВАЕМЫХ товаров после скидок (промокод + списанные баллы).
 * Подарок не засчитывается в свой же порог, поэтому из суммы вычитается цена ОДНОЙ
 * единицы подарочного блюда, лежащего в корзине — ровно как это делает касса
 * (`GiftTierResolverService.pickGiftForCart` на бэке).
 *
 * ⚠️ Подарочная позиция ищется по id товара (цепочки подарков активных шагов), а НЕ по
 * флагу `isGift`: сервер этот флаг не хранит, и после перезагрузки корзины он теряется.
 * Привязка к id делает расчёт устойчивым к перезагрузке.
 */

/** Шаг-подарок в терминах витрины (ответ GET /average-check/tiers/:branchId). */
export interface GiftTier {
  id: string
  threshold: number
  /** Цепочка id подарков: основной + план Б. Берём первый доступный в меню. */
  giftProductIds: string[]
}

/** Позиция корзины для расчёта базы. */
export interface GiftBaseItem {
  id?: string | number
  price: number | string
  quantity: number | string
}

/** Сумма ВСЕХ позиций корзины после скидок — зеркало `itemsAmount` на кассе. */
export function computeCartAmount(
  items: GiftBaseItem[],
  promocodeDiscount: number,
  pointsUsed: number,
): number {
  const goods = items.reduce(
    (sum, item) => sum + Number(item.price) * Number(item.quantity),
    0,
  )
  return Math.max(0, goods - (promocodeDiscount || 0) - (pointsUsed || 0))
}

/** Множество id всех подарков активных шагов (основные + планы Б). */
export function collectGiftProductIds(tiers: GiftTier[]): Set<string> {
  const ids = new Set<string>()
  for (const tier of tiers) {
    for (const id of tier.giftProductIds) ids.add(String(id))
  }
  return ids
}

/**
 * Цена ОДНОЙ единицы подарочного блюда, лежащего в корзине (0, если его там нет).
 * Бесплатна ровно одна единица — остальные оплачиваются, поэтому quantity не учитываем.
 */
export function findGiftUnitPrice(items: GiftBaseItem[], giftProductIds: Set<string>): number {
  const line = items.find(item => giftProductIds.has(String(item.id)))
  return line ? Number(line.price) || 0 : 0
}

/**
 * База порогов: сумма корзины после скидок минус одна единица подарка, если он уже
 * в корзине. Не ниже нуля.
 *
 * Величина УСТОЙЧИВА к выдаче подарка: добавление подарка увеличивает сумму корзины
 * ровно на ту цену, которая тут же вычитается. Без этого выдача подарка поднимала бы
 * базу и могла бы «дотянуть» до следующего шага, а замена подарка — уронить её обратно
 * (бесконечная чехарда подарков в корзине).
 */
export function computeGiftBase(
  items: GiftBaseItem[],
  promocodeDiscount: number,
  pointsUsed: number,
  giftProductIds: Set<string>,
): number {
  const amount = computeCartAmount(items, promocodeDiscount, pointsUsed)
  return Math.max(0, amount - findGiftUnitPrice(items, giftProductIds))
}

/** Результат подбора: достигнутый шаг и следующий (для подсказки). */
export interface GiftTierSelection {
  /** Достигнутый шаг (максимальный порог ≤ базы) или null. */
  current: GiftTier | null
  /** Следующий шаг (минимальный порог > базы) или null, если это уже вершина. */
  next: GiftTier | null
}

/**
 * Подбор достигнутого и следующего шага.
 * @param tiers шаги, у которых подарок ДОСТУПЕН (отфильтровано заранее).
 * @param base база порогов.
 */
export function pickGiftTier(tiers: GiftTier[], base: number): GiftTierSelection {
  const sorted = [...tiers].sort((a, b) => a.threshold - b.threshold)
  let current: GiftTier | null = null
  let next: GiftTier | null = null

  for (const tier of sorted) {
    if (base >= tier.threshold) {
      current = tier // держим последний (наибольший достигнутый порог)
    } else if (next === null) {
      next = tier // первый недостигнутый
    }
  }

  return { current, next }
}
